import { useState, type ReactNode } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Field";
import { useT } from "../../lib/i18n";
import type { Level } from "../../lib/model";

/**
 * 关卡列表类页面共用的筛选条（总造价 / 关卡 / 奖励）。
 *
 * 三页各写一遍「一个搜索框」是不够的：策划真正要的是**关卡区间**——
 * 「看一眼 20~30 关这段的曲线」，用关键字搜不出来。区间 + 城市名两个入口
 * 合起来才覆盖得住，所以做成一个 hook，三页共用同一份行为与文案。
 *
 * 只回筛选控件，不回整条 Toolbar：各页右侧要挂的动作按钮不一样。
 */
export function useLevelFilter(levels: Level[], note?: string) {
  const t = useT();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [kw, setKw] = useState("");

  const a = Number(from);
  const b = Number(to);
  const key = kw.trim().toLowerCase();
  const rows = levels.filter(
    (l) =>
      (!from || l.id >= a) &&
      (!to || l.id <= b) &&
      (!key || l.name.toLowerCase().includes(key) || String(l.id) === key),
  );
  const ranged = rows.length !== levels.length;

  const num = (v: string) => v.replace(/[^0-9]/g, "");
  const filters: ReactNode = (
    <>
      <span className="text-sm text-ink-regular">{t("flt.range")}</span>
      <Input value={from} onChange={(v) => setFrom(num(v))} align="right" placeholder={t("flt.from")} className="w-[72px]" />
      <span className="text-ink-placeholder">~</span>
      <Input value={to} onChange={(v) => setTo(num(v))} align="right" placeholder={t("flt.to")} className="w-[72px]" />
      <span className="ml-2 text-sm text-ink-regular">{t("flt.city")}</span>
      <Input value={kw} onChange={setKw} placeholder={t("flt.cityPlaceholder")} className="w-[190px]" />
      <Button size="sm" disabled={!from && !to && !kw} onClick={() => { setFrom(""); setTo(""); setKw(""); }}>
        {t("flt.reset")}
      </Button>
      <span className="text-xs text-ink-placeholder">
        {ranged
          ? t("flt.filtered", { n: rows.length, total: levels.length }) + (note ? `（${note}）` : "")
          : t("flt.all", { n: levels.length })}
      </span>
    </>
  );

  return { rows, filters, ranged };
}
